import type { VetorTabletop } from './tipos'

/**
 * Estática didática: o cálculo analítico que a simulação (Rapier) precisa
 * reproduzir. A física mostra o comportamento; estes números são a régua com a
 * qual cada valor simulado é conferido (ver `src/fisica/`).
 *
 * TypeScript puro: sem React, sem Three.js e sem o motor de física.
 */

export const GRAVIDADE_M_S2 = 9.80665

const rad = (graus: number) => (graus * Math.PI) / 180
const graus = (radianos: number) => (radianos * 180) / Math.PI

// ---------------------------------------------------------------------------
// Escada apoiada
// ---------------------------------------------------------------------------

export interface EntradaEscadaApoiada {
  /** Ângulo da escada com o chão, em graus (0–90). */
  readonly anguloGraus: number
  readonly massaEscadaKg: number
  /** Carga no ponto `posicaoCargaFracao` da escada (uma pessoa, uma maca içada). */
  readonly massaCargaKg: number
  /** Onde a carga está, de 0 (pé) a 1 (topo). */
  readonly posicaoCargaFracao: number
  /** Coeficiente de atrito do pé com o chão. */
  readonly atritoPe: number
  /**
   * Inclinação do chão sob o pé, em graus, no plano da escada: positiva quando o
   * chão sobe em direção à parede (o pé está no ponto baixo), negativa quando
   * desce. Sem ela, chão plano.
   */
  readonly inclinacaoChaoGraus?: number
}

export interface ResultadoEscadaApoiada {
  /** Reação horizontal da parede (lisa) no topo, em N. */
  readonly reacaoParedeN: number
  /** Atrito que o chão precisa fazer para a escada não escorregar, em N. */
  readonly atritoNecessarioN: number
  /** Atrito máximo que o chão pode fazer (μ·N), em N. */
  readonly atritoDisponivelN: number
  /** Menor ângulo em que a escada ainda não escorrega, em graus. */
  readonly anguloMinimoGraus: number
  readonly estavel: boolean
  /** `atritoDisponivel / atritoNecessario`: acima de 1 é estável; `Infinity` na vertical. */
  readonly margemDeAtrito: number
}

/**
 * Escada apoiada num plano vertical LISO (sem atrito no topo), com atrito no
 * pé. Equilíbrio dos momentos em torno do pé, com `θ` o ângulo com o chão:
 *
 *   N_parede · L·sen θ = (m_e·L/2 + m_c·s·L) · g · cos θ
 *   N_parede = (m_e/2 + m_c·s) · g / tg θ
 *
 * No chão: a normal vale (m_e + m_c)·g e o atrito necessário, igual à reação da
 * parede. A escada não escorrega enquanto `atrito ≤ μ · normal`, o que dá
 *
 *   tg θ_mín = (m_e/2 + m_c·s) / (μ · (m_e + m_c))
 *
 * Chão inclinado `α` sob o pé (sobe em direção à parede): a reação do chão é
 * (N_parede, peso) e se decompõe em normal (−N·sen α + P·cos α) e tangente
 * (N·cos α + P·sen α). O cone de atrito dá
 *
 *   N_parede,máx = P · tg(φ − α),   tg φ = μ
 *
 * e o ângulo mínimo sai de tg θ_mín = (m_e/2 + m_c·s)·g / N_parede,máx. Com
 * α = 0 volta à fórmula acima; se φ ≤ α, nenhum ângulo segura a escada.
 *
 * Não considera atrito no topo, flexão da escada nem o deslocamento da carga.
 */
export function analisarEscadaApoiada(entrada: EntradaEscadaApoiada): ResultadoEscadaApoiada {
  const { anguloGraus, massaEscadaKg: me, massaCargaKg: mc, posicaoCargaFracao: s, atritoPe: mu } = entrada
  const alfa = rad(entrada.inclinacaoChaoGraus ?? 0)
  const g = GRAVIDADE_M_S2
  const peso = (me + mc) * g
  const momento = (me / 2 + mc * s) * g
  const tangente = Math.tan(rad(anguloGraus))
  // na vertical a parede não faz força nenhuma: o peso passa pelo pé
  const reacaoParede = anguloGraus >= 90 ? 0 : momento / tangente
  // a reação do chão no pé é (N_parede, peso); decompõe em normal e tangente ao chão
  const normalChao = -reacaoParede * Math.sin(alfa) + peso * Math.cos(alfa)
  const atritoNecessario = Math.abs(reacaoParede * Math.cos(alfa) + peso * Math.sin(alfa))
  const atritoDisponivel = mu * Math.max(0, normalChao)
  // N_parede máx = peso · tg(φ − α), com tg φ = μ; se φ ≤ α o chão não segura nem a escada vertical
  const folgaAngular = Math.atan(mu) - alfa
  const reacaoMaxima = folgaAngular > 0 ? peso * Math.tan(folgaAngular) : 0
  const tanMinima = reacaoMaxima > 0 ? momento / reacaoMaxima : Infinity
  return {
    reacaoParedeN: reacaoParede,
    atritoNecessarioN: atritoNecessario,
    atritoDisponivelN: atritoDisponivel,
    anguloMinimoGraus: Number.isFinite(tanMinima) ? Math.min(90, graus(Math.atan(tanMinima))) : 90,
    estavel: atritoNecessario <= atritoDisponivel,
    margemDeAtrito: atritoNecessario === 0 ? Infinity : atritoDisponivel / atritoNecessario,
  }
}

/**
 * Inclinação, em graus, do perfil do chão logo em volta do pé: pontos
 * `[distância ao longo da escada até a parede, altura em relação ao pé]`.
 * Média numa janela de `±janelaM` (padrão 25 cm), porque a quina de um talude
 * ou de um degrau não tem inclinação única; sobe em direção à parede = positivo.
 */
export function inclinacaoDoPerfilNoPe(
  perfil: ReadonlyArray<readonly [number, number]>,
  janelaM = 0.25,
): number {
  const altura = (d: number) => {
    if (perfil.length === 0) return 0
    if (d <= perfil[0]![0]) return perfil[0]![1]
    for (let i = 1; i < perfil.length; i += 1) {
      const [d1, y1] = perfil[i]!
      if (d <= d1) {
        const [d0, y0] = perfil[i - 1]!
        return d1 === d0 ? y1 : y0 + ((y1 - y0) * (d - d0)) / (d1 - d0)
      }
    }
    return perfil[perfil.length - 1]![1]
  }
  return graus(Math.atan2(altura(janelaM) - altura(-janelaM), 2 * janelaM))
}

// ---------------------------------------------------------------------------
// Ancoragem em V
// ---------------------------------------------------------------------------

type V3 = readonly [number, number, number]

const subtrair = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const norma = (a: V3) => Math.hypot(a[0], a[1], a[2])
const escalar = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k]
const produto = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]

export interface ResultadoVFixa {
  /** Tensão em cada perna, em N; negativa significa que a perna ficaria frouxa. */
  readonly tensoesN: readonly [number, number]
  /** O que sobra do equilíbrio (carga que as pernas não seguram), em N: diferente de zero se o peso sai do plano do V. */
  readonly residuoN: number
  readonly frouxa: boolean
}

/**
 * V com as pernas FIXAS (comprimentos que não mudam, sem equalização): é um
 * sistema determinado. A carga é o peso de `massaKg` no ponto central e as duas
 * tensões saem de `T_a·u_a + T_b·u_b = (0, m·g, 0)` por mínimos quadrados;
 * `residuoN` mede quanto o peso foge do plano das duas pernas.
 */
export function analisarVFixa(centro: VetorTabletop, fixacaoA: VetorTabletop, fixacaoB: VetorTabletop, massaKg: number): ResultadoVFixa {
  const peso: V3 = [0, massaKg * GRAVIDADE_M_S2, 0]
  const da = subtrair(fixacaoA, centro)
  const db = subtrair(fixacaoB, centro)
  const ua = escalar(da, 1 / norma(da))
  const ub = escalar(db, 1 / norma(db))
  // equações normais: [ua·ua ua·ub; ua·ub ub·ub] [Ta Tb]^T = [ua·F ub·F]^T
  const a11 = produto(ua, ua)
  const a12 = produto(ua, ub)
  const a22 = produto(ub, ub)
  const det = a11 * a22 - a12 * a12
  if (det < 1e-12) return { tensoesN: [Infinity, Infinity], residuoN: Infinity, frouxa: false }
  const b1 = produto(ua, peso)
  const b2 = produto(ub, peso)
  const ta = (b1 * a22 - b2 * a12) / det
  const tb = (a11 * b2 - a12 * b1) / det
  const resto: V3 = [peso[0] - ta * ua[0] - tb * ub[0], peso[1] - ta * ua[1] - tb * ub[1], peso[2] - ta * ua[2] - tb * ub[2]]
  return { tensoesN: [ta, tb], residuoN: norma(resto), frouxa: ta < 0 || tb < 0 }
}

export interface ResultadoVEqualizada {
  /** Onde o ponto central assenta, no mundo. */
  readonly centro: VetorTabletop
  /** Tensão da corda, igual nas duas pernas, em N. */
  readonly tensaoN: number
  /** Ângulo de cada perna com a vertical, em graus (iguais nas duas). */
  readonly anguloComVerticalGraus: number
}

/**
 * V EQUALIZADO: uma corda só, com `comprimentoM` no total, passa pelas duas
 * fixações e leva o anel da carga. Sem atrito, a tensão é a mesma nos dois
 * lados e o anel desliza até o ponto mais baixo da elipse com focos nas
 * fixações, onde as duas pernas fazem o mesmo ângulo `φ` com a vertical:
 *
 *   T = m·g / (2·cos φ)
 *
 * As fixações e o anel ficam no mesmo plano vertical (a elipse é plana); `Z` é
 * mantido igual ao das fixações. Com `rigidezNPorM` finita a corda estica
 * `T / k` e o comprimento efetivo cresce; sem ela a corda é inextensível.
 */
export function equilibrioVEqualizado(
  fixacaoA: VetorTabletop,
  fixacaoB: VetorTabletop,
  comprimentoM: number,
  massaKg: number,
  rigidezNPorM = Infinity,
): ResultadoVEqualizada {
  const peso = massaKg * GRAVIDADE_M_S2
  // eixo horizontal do plano: de A para B no plano XZ
  const dx = fixacaoB[0] - fixacaoA[0]
  const dz = fixacaoB[2] - fixacaoA[2]
  const baseH = Math.hypot(dx, dz)
  const ex = baseH > 1e-9 ? dx / baseH : 1
  const ez = baseH > 1e-9 ? dz / baseH : 0
  const ya = fixacaoA[1]
  const yb = fixacaoB[1]

  /** Ponto mais baixo da elipse de comprimento total L: onde as pernas fazem o mesmo ângulo com a vertical. */
  const assentar = (L: number) => {
    // f(h) = diferença das componentes horizontais unitárias; h é a posição horizontal do anel, de A (0) para B (baseH)
    // para h entre os pés, y é fixado pela condição |A-C| + |B-C| = L: resolve-se y por bisseção para cada h
    const y = (h: number) => {
      // procura y abaixo de min(ya, yb) com |A-C| + |B-C| = L; a soma cresce à medida que y desce
      let alto = Math.min(ya, yb)
      let baixo = alto - L
      for (let i = 0; i < 80; i += 1) {
        const meio = (alto + baixo) / 2
        const soma = Math.hypot(h, meio - ya) + Math.hypot(baseH - h, meio - yb)
        // `meio` fundo demais (soma > L): a solução está acima dele
        if (soma > L) baixo = meio
        else alto = meio
      }
      return (alto + baixo) / 2
    }
    // o ponto mais baixo é onde as componentes horizontais unitárias das duas pernas se anulam:
    // h/|A-C| - (baseH-h)/|B-C| = 0, negativa em h=0 e positiva em h=baseH; bisseção
    const desequilibrio = (h: number) => {
      const yh = y(h)
      return h / Math.hypot(h, yh - ya) - (baseH - h) / Math.hypot(baseH - h, yh - yb)
    }
    let a = 0
    let b = baseH
    for (let i = 0; i < 80; i += 1) {
      const meio = (a + b) / 2
      if (desequilibrio(meio) < 0) a = meio
      else b = meio
    }
    const h = (a + b) / 2
    return { h, y: y(h) }
  }

  // sem rigidez: um único passo; com rigidez, ponto fixo L_eff = L + T/k
  let tensao = 0
  let resultado = assentar(comprimentoM)
  for (let i = 0; i < 40; i += 1) {
    const la = Math.hypot(resultado.h, resultado.y - ya)
    const cosPhi = Math.abs(resultado.y - ya) / la
    tensao = peso / (2 * cosPhi)
    if (!Number.isFinite(rigidezNPorM)) break
    const proxima = assentar(comprimentoM + tensao / rigidezNPorM)
    if (Math.abs(proxima.y - resultado.y) < 1e-9) {
      resultado = proxima
      break
    }
    resultado = proxima
  }
  const la = Math.hypot(resultado.h, resultado.y - ya)
  const phi = Math.acos(Math.abs(resultado.y - ya) / la)
  return {
    centro: [fixacaoA[0] + ex * resultado.h, resultado.y, fixacaoA[2] + ez * resultado.h],
    tensaoN: tensao,
    anguloComVerticalGraus: graus(phi),
  }
}

/** Comprimento total de corda de um V equalizado que passa pelas fixações e pelo anel em `centro`. */
export function comprimentoDoV(centro: VetorTabletop, fixacaoA: VetorTabletop, fixacaoB: VetorTabletop): number {
  return norma(subtrair(fixacaoA, centro)) + norma(subtrair(fixacaoB, centro))
}
